// A metric tile with its own icon from the approved design, and optionally the
// recent history it summarises as a sparkline (stated in text beside it).

import type { ReactNode } from 'react';
import { CountUp, Sparkline } from './Charts.tsx';
import * as format from '../format.ts';
import { Icon, type IconName } from './Icon.tsx';

/**
 * A sparkline is drawn only once this many of its days have data. Fewer is not
 * a trend: one spike on a flat line reads as a pattern, and an empty line reads
 * as a broken chart. Below it the tile simply has no line.
 */
export const SPARK_MIN_DAYS = 5;

export function sparkWorthDrawing(values: readonly number[] | undefined): values is readonly number[] {
  return values !== undefined && values.filter((value) => value > 0).length >= SPARK_MIN_DAYS;
}

export function Metric({
  icon,
  label,
  value,
  caption,
  loading = false,
  spark,
  sparkLabel,
  sparkColour,
}: {
  readonly icon: IconName;
  readonly label: string;
  readonly value: number | string | undefined;
  readonly caption: string;
  readonly loading?: boolean;
  readonly spark?: readonly number[] | undefined;
  readonly sparkLabel?: string;
  readonly sparkColour?: string;
}): ReactNode {
  return (
    <div className="metric">
      <div className="label has-icon">
        <span className="metric-icon">
          <Icon name={icon} />
        </span>
        {label}
      </div>
      {/* An unknown count shows a dash. A zero where the answer is "we could not
          read it" is the one number a reviewer must never be shown. */}
      <div className="value">
        {typeof value === 'number' ? <CountUp value={value} format={format.count} /> : (value ?? (loading ? '…' : '—'))}
      </div>
      <div className="caption">{caption}</div>
      {!sparkWorthDrawing(spark) || sparkLabel === undefined ? null : (
        <>
          <Sparkline values={spark} {...(sparkColour === undefined ? {} : { colour: sparkColour })} />
          <span className="visually-hidden">
            Last 14 days, {sparkLabel}: {spark.join(', ')}
          </span>
        </>
      )}
    </div>
  );
}

